import { useState, useEffect } from "react";
import SharedFileChip from "@photon/chat-react/file-chip";
import { getFileExtention } from "../../utils/helpers";
import { imageExtensions } from "../../constants/const";
import mediaUrlAdapter from "../../api/Media/mediaUrlAdapter";
import logger from "../../services/Logger";

/**
 * Read-only file chip for displaying attached files in chat message bubbles.
 * Resolves a canonical asset ID to a short-lived URL when rendered.
 *
 * For editable file chips (with remove/progress), use FileUploadItem instead.
 *
 * @param {Object} attachment - Canonical attachment descriptor
 */
const FileChip = ({ attachment }) => {
  const [resolvedUrl, setResolvedUrl] = useState(null);
  const assetId = attachment?.asset_id;

  useEffect(() => {
    if (!assetId) {
      return;
    }
    let cancelled = false;
    mediaUrlAdapter.resolveAsset(assetId).then((resolvedAsset) => {
      if (!cancelled && resolvedAsset?.url) {
        setResolvedUrl(resolvedAsset.url);
      }
    }).catch((err) => {
      if (!cancelled) {
        logger.error("FileChip", "Failed to resolve asset URL", { err: err?.message, assetId });
      }
    });
    return () => { cancelled = true; };
  }, [assetId]);

  const VIDEO_EXTENSIONS = ["mp4", "mov", "avi", "webm", "mkv", "m4v"];
  const AUDIO_EXTENSIONS = ["mp3", "wav", "m4a", "ogg", "flac", "aac"];

  const displayName = attachment?.filename || "File";
  const ext = getFileExtention(displayName);
  const extLower = (ext || "").toLowerCase();
  const isImage = imageExtensions.includes(extLower);
  const isVideo = VIDEO_EXTENSIONS.includes(extLower);
  const isAudio = AUDIO_EXTENSIONS.includes(extLower);

  return (
    <SharedFileChip
      filename={displayName}
      extension={ext}
      kind={isImage ? "image" : isVideo ? "video" : isAudio ? "audio" : "file"}
      url={resolvedUrl}
    />
  );
};

export default FileChip;
